import React from 'react';
import { useApp } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  UserCheck, 
  Heart, 
  TrendingUp,
  AlertTriangle,
  Globe,
  MapPin,
  DollarSign,
  BarChart3,
  Star,
  Shield,
  Siren,
  FileText,
  Building2
} from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const monthKey = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${date.getMonth()}`;
};

const lastSixMonths = () => {
  const now = new Date();
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    return {
      key: monthKey(date),
      month: date.toLocaleString('en-US', { month: 'short' }),
    };
  });
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { metrics, emergencies, visits, transactions } = useApp();
  const months = lastSixMonths();
  const visitTrendData = months.map(item => ({
    month: item.month,
    visits: visits.filter(visit => monthKey(visit.scheduledStart || visit.scheduled_start || visit.scheduledDate || visit.scheduled_date) === item.key).length,
  }));
  const revenueData = months.map(item => ({
    month: item.month,
    revenue: transactions
      .filter(transaction => monthKey(transaction.completedAt || transaction.completed_at || transaction.createdAt || transaction.created_at) === item.key)
      .reduce((sum, transaction) => sum + Number(transaction.amount || 0), 0),
  }));

  const planData = metrics?.planDistribution ? [
    { name: 'Essential', value: metrics.planDistribution.Essential, color: '#9F1D35' },
    { name: 'Comfort', value: metrics.planDistribution.Comfort, color: 'var(--color-gold-500)' },
    { name: 'Prestige', value: metrics.planDistribution.Prestige, color: '#22c55e' },
  ] : [];

  const alertCategoryData = metrics?.topAlertCategories ? Object.entries(metrics.topAlertCategories).map(([name, value]) => ({
    name,
    value,
  })) : [];
  const maxAlertCategoryValue = Math.max(1, ...alertCategoryData.map(item => Number(item.value) || 0));

  const geoData = metrics?.subscriberGeographies ? Object.entries(metrics.subscriberGeographies).map(([name, value]) => ({
    name,
    value,
  })) : [];
  const maxGeoValue = Math.max(1, ...geoData.map(item => Number(item.value) || 0));

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(num);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const directoryLinks = [
    { label: 'Subscribers', value: metrics?.totalActiveSubscribers || 0, path: '/subscribers', icon: Users },
    { label: 'Dependants', value: metrics?.totalDependantsEnrolled || 0, path: '/dependants', icon: Heart },
    { label: 'Visits', value: visits.length, path: '/visits', icon: FileText },
    { label: 'Care Officers', value: metrics?.activeCareOfficers || 0, path: '/officers', icon: UserCheck },
    { label: 'Supervisors', value: 'Manage', path: '/supervisors', icon: Shield },
    { label: 'Vendors', value: 'Manage', path: '/partners', icon: Building2 },
  ];

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-serif text-2xl font-bold text-gray-900">Platform Analytics</h1>
              <p className="text-sm text-gray-500 mt-1">Operations overview and key metrics</p>
            </div>
            <span className="px-3 py-1.5 bg-burgundy-100 text-burgundy-700 text-xs font-medium rounded-full">
              Live Data
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-burgundy-100 p-5">
            <div className="flex items-start justify-between mb-2">
              <Users className="w-5 h-5 text-burgundy-600" />
              <span className="text-xs text-green-600 flex items-center font-medium">
                <TrendingUp className="w-3 h-3 mr-0.5" />
                {metrics?.monthlyGrowthRate || 0}%
              </span>
            </div>
            <p className="text-3xl font-serif font-bold text-gray-900">{formatNumber(metrics?.totalActiveSubscribers || 0)}</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">Active Subscribers</p>
          </div>

          <div className="bg-white rounded-2xl border border-red-100 p-5">
            <div className="flex items-start justify-between mb-2">
              <Heart className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-3xl font-serif font-bold text-gray-900">{formatNumber(metrics?.totalDependantsEnrolled || 0)}</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">Dependants</p>
          </div>

          <div className="bg-white rounded-2xl border border-green-100 p-5">
            <div className="flex items-start justify-between mb-2">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-serif font-bold text-gray-900">{metrics?.activeCareOfficers || 0}</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">Care Officers</p>
          </div>

          <div className="bg-white rounded-2xl border border-green-100 p-5">
            <div className="flex items-start justify-between mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <span className="text-xs text-green-600 font-medium">MRR</span>
            </div>
            <p className="text-3xl font-serif font-bold text-green-600">{formatCurrency(metrics?.mrr || 0)}</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">Monthly Revenue</p>
          </div>

          <div className="bg-white rounded-2xl border border-blue-100 p-5">
            <p className="text-3xl font-serif font-bold text-gray-900">{metrics?.averageWellnessScore || 0}</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">Avg Wellness</p>
          </div>

          <div className="bg-white rounded-2xl border border-yellow-100 p-5">
            <div className="flex items-start justify-between mb-2">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            </div>
            <p className="text-3xl font-serif font-bold text-gray-900">{metrics?.averageCareOfficerRating || 0}</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">Avg CO Rating</p>
          </div>

          <div className="bg-white rounded-2xl border border-blue-100 p-5">
            <div className="flex items-start justify-between mb-2">
              <Shield className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-serif font-bold text-gray-900">{metrics?.averageVerificationScore || 0}</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">Avg Verification</p>
          </div>

          <div 
            className="bg-white rounded-2xl border border-red-100 p-5 cursor-pointer hover:border-red-300 transition-colors"
            onClick={() => navigate('/emergency-analytics')}
          >
            <div className="flex items-start justify-between mb-2">
              <Siren className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-3xl font-serif font-bold text-gray-900">{emergencies?.length || 0}</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">Emergencies</p>
          </div>
        </div>

        <div className="mb-8 rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">System Directory</h2>
              <p className="text-sm text-[#5A5A5A]">Admin shortcuts to the main records in Magnolia.</p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            {directoryLinks.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className="rounded-2xl border border-[#E8E6E3] bg-white p-4 text-left transition-colors hover:border-[#D8D5D0]"
                >
                  <Icon className="mb-3 h-5 w-5 text-[#9F1D35]" />
                  <p className="font-serif text-2xl font-semibold text-[#1A1A1A]">{item.value}</p>
                  <p className="mt-1 text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">{item.label}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Visit Trends */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-serif text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-gray-400" />
                Visit Volume Trend
              </h3>
            </div>
            <div className="p-5">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={visitTrendData}>
                    <defs>
                      <linearGradient id="visitGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#9F1D35" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#9F1D35" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="visits" 
                      stroke="#9F1D35" 
                      strokeWidth={2}
                      fill="url(#visitGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Revenue Trend */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-serif text-lg font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-gray-400" />
                Revenue Trend (USD)
              </h3>
            </div>
            <div className="p-5">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
                    <Tooltip 
                      contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                      formatter={(value) => [`$${formatNumber(value)}`, 'Revenue']}
                    />
                    <Bar dataKey="revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Plan Distribution */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900">Plan Distribution</h3>
            </div>
            <div className="p-5">
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={planData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={70}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {planData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 mt-2">
                {planData.map((item) => (
                  <div key={item.name} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-xs text-gray-600">{item.name} ({item.value})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Alert Categories */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900">Alert Categories</h3>
            </div>
            <div className="p-5">
              <div className="space-y-4">
                {alertCategoryData.map((item) => (
                  <div key={item.name}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-gray-600">{item.name}</span>
                      <span className="font-medium text-gray-900">{item.value}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-burgundy-500 rounded-full transition-all" style={{ width: `${Math.min(100, (Number(item.value) / maxAlertCategoryValue) * 100)}%` }} />
                    </div>
                  </div>
                ))}
                {alertCategoryData.length === 0 && (
                  <p className="text-sm text-gray-500">No alert categories yet.</p>
                )}
              </div>
            </div>
          </div>

          {/* Subscriber Geography */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900">Subscriber Geography</h3>
            </div>
            <div className="p-5">
              <div className="space-y-4">
                {geoData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-burgundy-500 rounded-full" style={{ width: `${Math.min(100, (Number(item.value) / maxGeoValue) * 100)}%` }} />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-10 text-right">{item.value}</span>
                    </div>
                  </div>
                ))}
                {geoData.length === 0 && (
                  <p className="text-sm text-gray-500">No subscriber geography data yet.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-green-100 p-5 text-center">
            <p className="text-3xl font-serif font-bold text-green-600">{metrics?.subscriberRetention6Month || 0}%</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">6-Month Retention</p>
          </div>
          <div className="bg-white rounded-2xl border border-burgundy-100 p-5 text-center">
            <p className="text-3xl font-serif font-bold text-burgundy-600">{metrics?.careOfficerCompletionRate || 0}%</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">CO Completion Rate</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 text-center">
            <p className="text-3xl font-serif font-bold text-gray-900">{metrics?.subscriberReportViewRate || 0}%</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">Report View Rate</p>
          </div>
          <div className="bg-white rounded-2xl border border-green-100 p-5 text-center">
            <p className="text-3xl font-serif font-bold text-green-600">+{metrics?.nps || 0}</p>
            <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">Net Promoter Score</p>
          </div>
        </div>

        {/* Coverage Overview */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-serif text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-400" />
              Coverage Overview
            </h3>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Countries Served</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(metrics?.countriesServed || {}).map(([country, percent]) => (
                    <span key={country} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                      {country}: {percent}
                    </span>
                  ))}
                  {Object.keys(metrics?.countriesServed || {}).length === 0 && (
                    <span className="text-sm text-gray-500">No country coverage data yet.</span>
                  )}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">States Covered (Nigeria)</h4>
                <div className="flex flex-wrap gap-2">
                  {(metrics?.statesCovered || []).map(state => (
                    <span key={state} className="px-3 py-1.5 bg-burgundy-100 text-burgundy-700 text-xs font-medium rounded-full">
                      {state}
                    </span>
                  ))}
                  {(metrics?.statesCovered || []).length === 0 && (
                    <span className="text-sm text-gray-500">No state coverage data yet.</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
